/**
 * Copy, options and constants for the education-spine onboarding.
 * Mirrors PeakApple `Onboarding/OnboardingState.swift` (OnboardingCopy,
 * OnboardingCompetitionLevel, OnboardingSex, OnboardingWellnessPreview).
 * Keep strings verbatim with iOS so the two apps read the same.
 */

export const COMPETITION_LEVELS = [
  "Olympics or World Championships",
  "National representation",
  "Professional league",
  "Semi-professional / sub-elite",
  "State or regional",
  "Club or community",
  "School or junior pathway",
  "Training, not currently competing",
] as const;
export type CompetitionLevel = (typeof COMPETITION_LEVELS)[number];

export const SEX_OPTIONS: Array<{ label: string; wire: "female" | "male" }> = [
  { label: "Female", wire: "female" },
  { label: "Male", wire: "male" },
];
export type OnboardingSex = "female" | "male";

export const DEFAULT_DOB = "1998-06-15";
export const DOB_MIN = "1920-01-01";
export const DEMO_PEAK_SCORE = 82;
export const DEMO_WELLNESS_SCORE = 77;

export const WELCOME_SLIDES: Array<{ title: string; body: string }> = [
  { title: "Ready to train like an athlete?", body: "Of course you are..." },
  {
    title: "Know Thyself.",
    body: "Elite athletes practice self-awareness and data driven feedback loops.",
  },
  {
    title: "How it works?",
    body: "The more you log, the better your insights get. PEAK^ builds an intelligence model entirely based on you using old school wisdom and modern tech.",
  },
];
export const WELCOME_CREDITS_INDEX = 2;
export const WELCOME_PHILOSOPHY = "philosophy";
export const WELCOME_SOCRATES = "— Socrates";
export const WELCOME_INSIGHT_LEAD =
  "Elite athletes practice self-awareness and data driven feedback loops.";
export const WELCOME_HELPS = [
  "know when to push",
  "know when to recover",
  "individualise your training plan",
];
export const KNOW_THYSELF_CREDITS = [
  "Subjective Wellness",
  "Training Load (RPE x Time)",
  "Load Balance",
  "Activities (Exercise, Set, Reps...)",
  "Wearable Data",
  "Heart Lung Recovery",
  "Peak Readiness",
  "Individualised Insights",
  "Performances",
  "Goals",
  "Niggles",
  "Injuries",
  "Performance Reports",
  "Perceived Effort",
];

/** Seconds; loops with the Know Thyself colour-wheel video. */
export const READINESS_LOOP_SECONDS = 22.09;
export interface ReadinessCue {
  at: number;
  label: string;
  color: string;
}
export const READINESS_CUES: ReadinessCue[] = [
  { at: 0, label: "Injury risk", color: "#EF4444" },
  { at: 4.22, label: "Caution", color: "#F97316" },
  { at: 8.2, label: "Building", color: "#22C55E" },
  { at: 14, label: "Balanced, Push", color: "#0096C7" },
  { at: 18.22, label: "Peaking Zone, go for it.", color: "#D4AF37" },
];
export const readinessCueAt = (elapsed: number): ReadinessCue => {
  const t = ((elapsed % READINESS_LOOP_SECONDS) + READINESS_LOOP_SECONDS) % READINESS_LOOP_SECONDS;
  let cue = READINESS_CUES[0];
  for (const c of READINESS_CUES) if (t >= c.at) cue = c;
  return cue;
};

/** Readiness bands (PeakScoreHelpSheet.swift `PeakScoreZone`). */
export interface PeakScoreZone {
  name: string;
  range: string;
  detail: string;
  lower: number;
  upper: number;
  color: string;
}
export const PEAK_SCORE_ZONES: PeakScoreZone[] = [
  { name: "Strong Caution", range: "0–29", detail: "Low readiness, prioritise recovery and rest today.", lower: 0, upper: 30, color: "#EF4444" },
  { name: "Average Readiness", range: "30–59", detail: "Room for improvement in wellness & training load balance.", lower: 30, upper: 60, color: "#F97316" },
  { name: "Good Readiness for building", range: "60–79", detail: "Suitable for all training but monitor warm up.", lower: 60, upper: 80, color: "#22C55E" },
  { name: "Excellent Readiness", range: "80–100", detail: "Optimal for high-intensity training.", lower: 80, upper: 100, color: "#0096C7" },
  { name: "Golden Zone", range: ">100", detail: "Ideal conditions for possible personal best today.", lower: 100, upper: 110, color: "#D4AF37" },
];
export const PEAK_SCORE_SCALE_MAX = 110;
export const peakScoreZone = (score: number): PeakScoreZone => {
  if (score > 100) return PEAK_SCORE_ZONES[4];
  if (score >= 80) return PEAK_SCORE_ZONES[3];
  if (score >= 60) return PEAK_SCORE_ZONES[2];
  if (score >= 30) return PEAK_SCORE_ZONES[1];
  return PEAK_SCORE_ZONES[0];
};

/** Finish-loader status lines, in order (OnboardingController.finish). */
export const FINISH_STATUS = {
  profile: "Creating your profile…",
  extras: "Saving your goal and sports…",
  email: "Sending your welcome email…",
  dashboard: "Getting the app ready…",
  checkout: "Preparing your secure checkout…",
} as const;

export const AMBASSADOR_COPY = {
  title: "Ambassador Code",
  subtitle: "Were you referred by an athlete ambassador? If so, enter their code here.",
  placeholder: "AMBASSADOR",
  apply: "Apply code",
  checking: "Checking…",
  skip: "No, I don't have a code.",
  notFound: "We couldn't find that ambassador code. Double check it and try again.",
  inactive: "That ambassador code isn't active right now.",
  thanks: (firstName: string) =>
    `Thank you for supporting ${firstName}, if you select a paid plan they will receive a commission.`,
} as const;

export const GOAL_SUBTEXT =
  "Write in your own words what you are training for and what your athletic goal is. This helps train your AI insights to be individualised to you.";

/** "The Sports Science" page of the readiness education. */
export const SPORTS_SCIENCE_LEAD =
  "Asking yourself how you feel before checking your wearable data is important.";
export const SPORTS_SCIENCE_BODY =
  "In 85% of studies where objective and subjective results differed, athletes' self-reported wellness was a more sensitive measure than the metrics wearables track, like heart rate and HRV.";
export const HLR_DETAIL =
  "Data from wearables - Resting Heart Rate, Heart Rate Variability & Respiratory Rate (Optional)";
export const SPORTS_SCIENCE_CITATION = "Saw AE, Main LC, Gastin PB. Br J Sports Med. 2016;50(5):281-291.";

/** iOS `OnboardingFirstWellnessPage` intro. */
export const FIRST_WELLNESS_INTRO = {
  lead: "No one (or wearable) knows how you feel better than you do.",
  paragraphs: [
    "A daily wellness check-in is widely considered one of the most sensitive and practical ways to monitor athlete readiness.",
    "Research shows that simple self-reported measures such as sleep quality, muscle soreness, mood, stress and fatigue often pick up changes in how your body is handling training more reliably than many objective measures (Saw, Main & Gastin, 2016).",
    "Taking a minute each day to check in helps you spot early signs of fatigue, adjust before small issues become bigger ones, and understand the patterns behind your best performances.",
    "Every check-in also contributes to your Peak Readiness Score, so the more consistently you track, the more accurate your score will be when you choose to generate it.",
  ],
  reference: SPORTS_SCIENCE_CITATION,
} as const;

// ── Wellness form (OnboardingWellnessPreview) ──────────────────────────────────

export const WELLNESS_CATEGORIES = ["mood", "recovery", "sleep", "stress", "health", "physical"] as const;
export type WellnessCategory = (typeof WELLNESS_CATEGORIES)[number];

export const SLEEP_TIME_RESPONSES = [
  "less than 4 hours",
  "4 to 5 hours",
  "5 to 6 hours",
  "6 to 6.5 hours",
  "6.5 to 7.5 hours",
  "7.5 to 8.5 hours",
  "8.5+ hours",
];

export const SORENESS_SCALE = [
  "No soreness.",
  "Barely noticeable tightness.",
  "Mild discomfort, no impact on movement.",
  "Slight soreness during specific movements.",
  "Noticeable soreness, but doesn't hinder performance.",
  "Moderate soreness, mildly affects some movement.",
  "Sore with discomfort during moderate activity.",
  "Significant soreness, alters movement patterns.",
  "Very sore, limits range of motion.",
  "Severe soreness, difficult to move without pain.",
  "Extremely sore and movement limiting.",
];

export const CAPACITY_OPTIONS: Array<{ wire: "full" | "modified" | "none"; label: string }> = [
  { wire: "full", label: "Full" },
  { wire: "modified", label: "Modified" },
  { wire: "none", label: "None" },
];

export const wellnessCategoryLabel = (category: WellnessCategory): string =>
  category === "stress" ? "Stress" : category.charAt(0).toUpperCase() + category.slice(1);

export const WELLNESS_FALLBACK_QUESTIONS: Record<WellnessCategory, { question: string; answers: string[] }> = {
  mood: {
    question: "How is your mood today?",
    answers: ["Low and flat", "A bit off", "Neutral", "Steady", "Positive and motivated", "Up and sharp", "Firing"],
  },
  recovery: {
    question: "How recovered do you feel?",
    answers: ["Wrecked", "Heavy legs", "Still tired", "Mostly back", "Ready to train", "Fresh", "Fully recovered"],
  },
  sleep: {
    question: "How did you sleep?",
    answers: ["Broken and unrested", "Restless night", "Okay sleep", "Mostly rested", "Rested and recovered", "Deeply recovered", "Best sleep in weeks"],
  },
  stress: {
    question: "How stressed do you feel?",
    answers: ["Overwhelmed", "Very tense", "On edge", "Manageable", "Mostly calm", "Settled", "Completely at ease"],
  },
  health: {
    question: "How is your health today?",
    answers: ["Really unwell", "Under the weather", "A bit off", "Okay", "Pretty good", "Healthy", "Firing on all cylinders"],
  },
  physical: {
    question: "How does your body feel?",
    answers: ["Beaten up", "Heavy and sore", "A bit battered", "Usable", "Moving well", "Light and ready", "Best I've felt"],
  },
};

export const wellnessAnswerLabel = (labels: string[], value: number): string => {
  const index = Math.min(Math.max(value, 1), labels.length) - 1;
  return labels[index];
};

export const sorenessLabel = (value: number): string =>
  SORENESS_SCALE[Math.min(Math.max(value, 0), SORENESS_SCALE.length - 1)];

export const TERMS_URL = "https://peak.app/terms-of-service";
